import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const MyEntriesSection = ({ entries, onBack }) => {
  const [filter, setFilter] = useState('all');
  const [sortBy, setSortBy] = useState('recent');

  const filterOptions = [
    { value: 'all', label: 'All Entries' },
    { value: 'confirmed', label: 'Confirmed' },
    { value: 'pending', label: 'Pending' },
    { value: 'winner', label: 'Winner' }
  ];

  const sortOptions = [
    { value: 'recent', label: 'Most Recent' },
    { value: 'oldest', label: 'Oldest First' },
    { value: 'title', label: 'Title A-Z' }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed':
        return 'bg-success-100 text-success-700';
      case 'pending':
        return 'bg-warning-100 text-warning-700';
      case 'winner':
        return 'bg-primary-100 text-primary-700';
      default:
        return 'bg-accent-100 text-accent-700';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'confirmed':
        return 'CheckCircle';
      case 'pending':
        return 'Clock';
      case 'winner':
        return 'Trophy';
      default:
        return 'AlertCircle';
    }
  };

  const filteredEntries = entries.filter(entry => {
    if (filter === 'all') return true;
    return entry.status === filter;
  });

  const sortedEntries = [...filteredEntries].sort((a, b) => {
    switch (sortBy) {
      case 'oldest':
        return new Date(a.entryDate) - new Date(b.entryDate);
      case 'title':
        return a.giveawayTitle.localeCompare(b.giveawayTitle);
      default: // recent
        return new Date(b.entryDate) - new Date(a.entryDate);
    }
  });

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div>
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <Button
          variant="ghost"
          onClick={onBack}
          iconName="ArrowLeft"
          iconPosition="left"
          className="text-accent-600 hover:text-accent-800"
        >
          Back to Giveaways
        </Button>
        
        <div className="flex items-center space-x-3">
          <div className="w-12 h-12 bg-gradient-to-br from-primary-400 to-secondary-400 rounded-2xl flex items-center justify-center shadow-warm">
            <Icon name="User" size={24} color="white" />
          </div>
          <div>
            <h1 className="font-heading font-bold text-3xl lg:text-4xl text-accent-800">
              My Entries
            </h1>
            <p className="text-accent-600 mt-1">
              Track your giveaway participation and results
            </p>
          </div>
        </div>
      </div>

      {/* Filters and Sort */}
      <div className="bg-white rounded-2xl shadow-md p-6 mb-8">
        <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
          <div className="flex flex-wrap gap-2">
            {filterOptions.map((option) => (
              <button
                key={option.value}
                onClick={() => setFilter(option.value)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  filter === option.value
                    ? 'bg-primary text-white' :'bg-surface hover:bg-accent-100 text-accent-700'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
          
          <div className="flex items-center gap-2">
            <span className="text-sm text-accent-600">Sort by:</span>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-3 py-2 border border-accent-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            >
              {sortOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Entries List */}
      {sortedEntries.length > 0 ? (
        <div className="space-y-4">
          {sortedEntries.map((entry) => (
            <div key={entry.id} className="bg-white rounded-2xl shadow-md p-6 hover:shadow-lg transition-shadow">
              <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                {/* Entry Info */}
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="font-heading font-semibold text-lg text-accent-800">
                      {entry.giveawayTitle}
                    </h3>
                    <div className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(entry.status)}`}>
                      <div className="flex items-center gap-1">
                        <Icon name={getStatusIcon(entry.status)} size={12} />
                        {entry.status.charAt(0).toUpperCase() + entry.status.slice(1)}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex flex-wrap gap-4 text-sm text-accent-600">
                    <div className="flex items-center gap-1">
                      <Icon name="Calendar" size={16} />
                      <span>Entered {formatDate(entry.entryDate)}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Icon name="Hash" size={16} />
                      <span>Entry {entry.entryNumber}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Icon name="Key" size={16} />
                      <span>Code: {entry.entryCode}</span>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="ExternalLink"
                    iconPosition="left"
                  >
                    View Details
                  </Button>
                  
                  {entry.status === 'confirmed' && (
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="Share"
                      iconPosition="left"
                      className="text-primary hover:text-primary-600"
                    >
                      Share
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-16">
          <div className="w-24 h-24 bg-accent-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Icon name="User" size={32} className="text-accent-400" />
          </div>
          <h3 className="font-heading font-semibold text-xl text-accent-800 mb-2">
            No entries found
          </h3>
          <p className="text-accent-600 mb-6 max-w-md mx-auto">
            {filter === 'all' ? "You haven't entered any giveaways yet. Browse available giveaways to get started!" : `No entries with status"${filter}". Try changing your filter.`
            }
          </p>
          <Button
            variant="primary"
            onClick={onBack}
            iconName="Gift"
            iconPosition="left"
            className="shadow-warm"
          >
            Browse Giveaways
          </Button>
        </div>
      )}

      {/* Statistics */}
      {entries.length > 0 && (
        <div className="mt-12 bg-white rounded-2xl shadow-md p-6">
          <h3 className="font-heading font-semibold text-lg text-accent-800 mb-4">
            Entry Statistics
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-primary mb-1">
                {entries.length}
              </div>
              <div className="text-sm text-accent-600">Total Entries</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-success mb-1">
                {entries.filter(e => e.status === 'confirmed').length}
              </div>
              <div className="text-sm text-accent-600">Confirmed</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-warning mb-1">
                {entries.filter(e => e.status === 'pending').length}
              </div>
              <div className="text-sm text-accent-600">Pending</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-primary mb-1">
                {entries.filter(e => e.status === 'winner').length}
              </div>
              <div className="text-sm text-accent-600">Won</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyEntriesSection;